import axios from '../../axiosConfig';
import { extractApiErrors } from '../../utils/apiErrors';
import { getMutationError } from '../../utils/userMutationFeedback';

const trimString = (value) => (typeof value === 'string' ? value.trim() : '');

export const toBrainRoleName = (roleName, scope) => {
  const normalizedName = trimString(roleName).toLowerCase();
  const normalizedScope = trimString(scope).toLowerCase();

  if (normalizedScope === 'organization') {
    if (['organization_admin', 'org_admin'].includes(normalizedName)) return 'org_master';
    if (normalizedName === 'organization_user') return 'org_user';
  }

  if (normalizedScope === 'network' && normalizedName === 'network_admin') {
    return 'network_manager';
  }

  return normalizedName;
};

const buildUserPayload = (payload = {}) => ({
  first_name: trimString(payload.first_name),
  last_name: trimString(payload.last_name),
  email: trimString(payload.email),
});

const toPositiveNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
};

const buildRoleAssignment = (context, payload = {}) => {
  const scope = trimString(payload.scope);
  const roleName = toBrainRoleName(payload.role_name || payload.roleName, scope);

  if (!roleName || !scope) return null;

  const assignment = {
    name: roleName,
    scope,
    consultant_id: null,
    network_id: null,
    organization_id: null,
  };

  const consultantId = toPositiveNumber(payload.consultant_id ?? payload.consultantId);
  const networkId = toPositiveNumber(payload.network_id ?? payload.networkId);
  const organizationId = toPositiveNumber(payload.organization_id ?? payload.organizationId);
  const contextId = toPositiveNumber(context?.id);

  if (scope === 'consultant') {
    assignment.consultant_id = consultantId ?? (context?.type === 'consultant' ? contextId : null);
  }

  if (scope === 'network') {
    assignment.network_id = networkId ?? (context?.type === 'network' ? contextId : null);
  }

  if (scope === 'organization') {
    assignment.organization_id = organizationId ?? (context?.type === 'organization' ? contextId : null);
  }

  return assignment;
};

const buildRequestBody = (context, payload = {}) => {
  const roleAssignment = buildRoleAssignment(context, payload);
  const requestBody = { user: buildUserPayload(payload) };

  if (roleAssignment) {
    requestBody.roles = [roleAssignment];
  }

  return requestBody;
};

const getCreateEndpoint = (context, roleAssignment) => {
  const type = context?.type;
  const namespace = trimString(context?.namespace).toLowerCase();
  const contextId = toPositiveNumber(context?.id);

  if (type === 'hke') return '/users';

  if (namespace === 'consultant') {
    if (roleAssignment?.scope === 'network' && roleAssignment?.network_id) {
      return `/consultant/networks/${roleAssignment.network_id}/users`;
    }

    if (roleAssignment?.scope === 'organization' && roleAssignment?.organization_id) {
      return `/consultant/organizations/${roleAssignment.organization_id}/users`;
    }

    if (roleAssignment?.scope === 'consultant') {
      throw new Error('Consultant-scoped user creation is not supported from consultant context.');
    }
  }

  switch (type) {
    case 'network':
      if (roleAssignment?.scope === 'network') {
        return '/users';
      }

      if (roleAssignment?.scope === 'organization') {
        return '/users';
      }

      return `/networks/${assertContextId(context)}/users`;
    case 'organization':
      return `/organizations/${assertContextId(context)}/users`;
    case 'consultant':
      if (!contextId) {
        throw new Error('Missing required context id for "consultant" user creation.');
      }
      return `/consultants/${contextId}/users`;
    default:
      throw new Error(`Unsupported user creation context "${type}".`);
  }
};

const assertContextId = (context) => {
  const id = Number(context?.id);
  if (!Number.isFinite(id) || id <= 0) {
    throw new Error(`Missing required context id for "${context?.type}" user creation.`);
  }
  return id;
};

const createHkeUser = async (payload = {}) => {
  return axios.post('/users', buildRequestBody({ type: 'hke' }, payload));
};

export const createUserInContext = (context, payload) => {
  if (context?.type === 'hke') {
    return createHkeUser(payload);
  }

  const endpoint = getCreateEndpoint(context, buildRoleAssignment(context, payload));
  return axios.post(endpoint, buildRequestBody(context, payload));
};

const normalizeMessage = (value) => {
  if (Array.isArray(value)) return value.join(', ');
  if (value == null) return '';
  return String(value);
};

export const parseCreateUserErrors = (error) => {
  const { status } = extractApiErrors(error);
  const data = error?.response?.data;
  const fieldErrors = {};
  const messages = [];

  const errors = data?.errors;
  if (Array.isArray(errors)) {
    messages.push(...errors.map(normalizeMessage).filter(Boolean));
  } else if (errors && typeof errors === 'object') {
    Object.entries(errors).forEach(([key, value]) => {
      const message = normalizeMessage(value);
      if (!message) return;
      fieldErrors[key] = message;
      messages.push(`${key.replace(/_/g, ' ')}: ${message}`);
    });
  }

  if (data?.error) messages.push(normalizeMessage(data.error));
  if (data?.message) messages.push(normalizeMessage(data.message));

  const uniqueMessages = Array.from(new Set(messages.filter(Boolean)));
  const isValidationError = status === 400 || status === 422;
  const fallbackMessage = getMutationError(error).message;

  if (uniqueMessages.length === 0 && fallbackMessage) {
    uniqueMessages.push(fallbackMessage);
  }

  return {
    status,
    fieldErrors,
    messages: uniqueMessages,
    isValidationError,
  };
};
